import { gql } from '@apollo/client';
import { Field, Formik, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { FormGroup, Input, Label } from 'reactstrap';
import * as yup from 'yup';
import styled from 'styled-components';
import { GROWING_ZONES } from '../../constants/growingZones';
import { DISTANCE_UNITS, YIELD_UNITS_BY_DEFAULT } from '../../constants/units';
import { useSetBreadcrumbEffect } from '../../contexts/BreadcrumbContext';
import {
  DeleteCropTemplateMutation,
  DeleteFarmCropMutation,
  UpsertCropTemplateMutation,
  UpsertFarmCropMutation,
} from '../../mutations';
import { useUnits, useUnitsMap } from '../../contexts/UnitContext';
import { useQueryWithFarmId, useRecord } from '../../utils/hooks';
import {
  getFarmPath,
  objectWithFields,
  thirtyYearArray,
} from '../../utils/utils';
import { defaultableNumber } from '../../utils/yup';
import FormTypeahead from '../FormTypeahead';
import CropActionsDropdown from './CropActionsDropdown';
import { CropTemplatesQuery, RawCropsQuery } from './CropsTable';
import DuplicateTemplateButton from './DuplicateTemplateButton';
import {
  DeleteButton,
  DropdownButtonField,
  Message,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
  WideFormGroup,
  YearInputs,
} from './template-components';
import CropVarietiesTypeahead from './CropVarietiesTypeahead';
import TableCsvButton from '../tables/TableCsvButton';
import { N_YEARS } from '../../constants/years';
import {
  asPercentage,
  numberAsCurrencyWithCents,
} from '../../utils/formatters';
import useCropExportFileName from '../../hooks/useCropExportFileName';

const FormSection = styled.div`
  flex-basis: 50%;
  flex-grow: 1;
  min-width: 360px;
`;

const DeletionTooltipMessage = styled(({ patterns, className }) => (
  <div className={className}>
    Cannot delete a crop that is used in patterns:
    <ul>
      {patterns.map(({ id: patternId, name }) => (
        <li key={patternId}>{name}</li>
      ))}
    </ul>
  </div>
))`
  ul {
    list-style-type: disc;
    margin-left: 1.5rem;
  }
`;

const fields = [
  'id',
  'name',
  'description',
  'betweenRowSpacing',
  'inRowSpacing',
  'mortality',
  'mortalityEscalator',
  'unitCost',
];

const varietyFields = ['id', 'name'];

const yieldFields = [
  'id',
  'targetYieldPerTree',
  'percentTargetYieldByYear',
  'unit',
];

const queryString = `
  updatedAt
  ${fields.join(' ')}
  cropVarieties {
    ${varietyFields.join(' ')}
  }
  yield {
    ${yieldFields.join(' ')}
  }
`;

const CropsForFarmQuery = gql`
  query CropsForFarmSettings($farmId: ID!) {
    crops(farmId: $farmId) {
      id
      name
    }
  }
`;

const CropTemplateQuery = gql`
  query CropTemplateSettings($id: ID!) {
    cropTemplate(id: $id) {
      ${queryString}
      growingZones
      isPublished
    }
  }
`;

const RawCropQuery = gql`
  query RawCropSettings($id: ID!) {
    rawCrop(id: $id) {
      ${queryString}
      templateId
      farm {
        id
        team {
          id
          allowedActions {
            id
            canEdit
          }
        }
      }
      inRowPatterns {
        id
        name
      }
    }
  }
`;

const exportCols = [
  { displayString: '', accessor: 'metric' },
  { displayString: '', accessor: 'value' },
  ...Array.from({ length: N_YEARS }, (_, i) => ({
    displayString: `Year ${i + 1}`,
    accessor: i,
    format: asPercentage,
  })),
];

const exportData = (resource, yieldUnits, distanceUnits) => {
  const {
    betweenRowSpacing,
    cropVarieties,
    description,
    growingZones,
    inRowSpacing,
    mortality,
    mortalityEscalator,
    unitCost,
    yield: yld,
  } = resource;
  const yieldUnit = yieldUnits[yld?.unit || 'lbs'];
  return [
    {
      metric:
        yld?.unit === 'bft'
          ? 'Percent of trees harvested'
          : 'Percent of target yield',
      ...Object.fromEntries(
        (yld?.percentTargetYieldByYear || []).map((percentYield, i) => [
          i,
          percentYield ?? 0,
        ]),
      ),
    },
    { metric: 'Unit cost', value: unitCost, format: numberAsCurrencyWithCents },
    { metric: 'Mortality', value: mortality, format: asPercentage },
    {
      metric: 'Mortality Escalator',
      value: mortalityEscalator,
      format: asPercentage,
    },
    {
      metric: 'Target yield per tree',
      value: yld?.targetYieldPerTree,
      format: yieldUnit.transformToUnit,
    },
    { metric: 'Yield unit', value: yieldUnit.displayCsv },
    // Include growing zones for crop templates
    ...(growingZones
      ? [{ metric: 'Growing zones', value: growingZones.join(', ') }]
      : []),
    {
      metric: `Between-row spacing (${distanceUnits.displayCsv})`,
      value: betweenRowSpacing,
      format: distanceUnits.transformToUnit,
    },
    {
      metric: `In-row spacing (${distanceUnits.displayCsv})`,
      value: inRowSpacing,
      format: distanceUnits.transformToUnit,
    },
    { metric: 'Description', value: description },
    { metric: 'Varieties', value: cropVarieties.map((v) => v.name).join(', ') },
  ];
};

const defaultCrop = {
  ...objectWithFields(fields),
  mortality: 0,
  mortalityEscalator: 5,
  yield: {
    ...objectWithFields(yieldFields),
    unit: 'lbs',
    percentTargetYieldByYear: thirtyYearArray(''),
  },
  growingZones: undefined, // Not included in farm crops
  cropVarieties: [],
};

const cropSettingsSchema = ({ requireGrowingZones }) =>
  yup.object().shape({
    name: yup.string().required('Required'),
    description: yup
      .string()
      .nullable()
      .max(10_000, 'Maximum length is 10,000 characters.'),
    cropVarieties: yup
      .array(
        yup.object().shape({
          name: yup.string().required('Required'),
        }),
      )
      .required('Required'),
    growingZones: requireGrowingZones
      ? yup.array(yup.string()).required('Required').min(1, 'Required')
      : undefined,
    betweenRowSpacing: yup
      .number()
      .positive('Must be positive')
      .required('Required'),
    inRowSpacing: yup
      .number()
      .positive('Must be positive')
      .required('Required'),
    unitCost: yup.number().min(0, 'Minimum is 0').required('Required'),
    mortality: yup
      .number()
      .min(0, 'Minimum is 0')
      .max(100, 'Maximum is 100')
      .required('Required'),
    mortalityEscalator: yup
      .number()
      .min(0, 'Minimum is 0')
      .required('Required'),
    yield: yup.object().shape({
      targetYieldPerTree: yup
        .number()
        .min(0, 'Minimum is 0')
        .required('Required'),
      unit: yup.string().required('Required'),
      percentTargetYieldByYear: yup
        .array()
        .of(
          defaultableNumber(0)
            .min(0, 'Minimum is 0')
            .max(200, 'Maximum is 200'),
        )
        .test(
          'has-100',
          'Must have a value that is 100%',
          // Note: cannot use `this` with an arrow function
          function has100(value) {
            return (
              this.parent.unit === 'bft' || Object.values(value).includes(100)
            );
          },
        )
        .required(),
    }),
  });

const transformValues = (values) => ({
  ...values,
  templateId: undefined,
  farm: undefined,
  __typename: undefined,
  yield: {
    ...values.yield,
    id: undefined,
    __typename: undefined,
  },
  cropVarieties: values.cropVarieties.map(
    ({ customOption, ...cropVariety }) => ({
      ...cropVariety,
      // Clear id before saving if this is a new variety
      id: customOption ? undefined : cropVariety.id,
      __typename: undefined,
    }),
  ),
  inRowPatterns: undefined,
});

const YieldSection = () => {
  const { values } = useFormikContext();
  return (
    <YearInputs
      accessor="yield.percentTargetYieldByYear"
      min={0}
      step="any"
      label={
        values?.yield?.unit === 'bft'
          ? 'Percent of trees harvested by year'
          : 'Percent target yield by year'
      }
    />
  );
};

const EditCropSettings = ({
  disableRename,
  onSubmit,
  onDelete,
  msg,
  resource,
  status,
  showGrowingZones,
  showPublishButton,
  renderButtons,
}) => {
  const { loading } = status;
  const { id, teamId } = useParams();
  const crop = resource
    ? { ...resource, yield: resource.yield || defaultCrop.yield }
    : defaultCrop;
  const schema = cropSettingsSchema({ requireGrowingZones: showGrowingZones });
  const distanceUnits = useUnits(DISTANCE_UNITS);
  const yieldUnits = useUnitsMap(YIELD_UNITS_BY_DEFAULT);
  const yieldUnitOptions = Object.entries(
    yieldUnits,
  ).map(([value, { displayUnit: name }]) => ({ value, name }));
  const title = useCropExportFileName({
    cropName: resource?.name,
    plural: false,
    title: 'Crop Settings',
  });

  // undefined is used to skip setting the value for an existing crop/template
  // because it's already set in EditCropView
  const breadcrumbs = id ? undefined : [{ display: 'New' }];
  if (breadcrumbs && teamId) {
    breadcrumbs.unshift({
      display: 'Crop Templates',
      url: ':rootUrl/templates',
    });
  }
  useSetBreadcrumbEffect(breadcrumbs, [id, teamId]);

  if (msg) return <Message>{msg}</Message>;

  const canBeDeleted = !crop.inRowPatterns?.length;
  const deletionTooltipContent = canBeDeleted ? undefined : (
    <DeletionTooltipMessage patterns={crop.inRowPatterns} />
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{ teamId, ...crop }}
      onSubmit={onSubmit(schema)}
      validationSchema={schema}
      className="mb-2"
    >
      {({ isSubmitting, values }) => (
        <TemplateForm>
          <div className="d-flex flex-wrap">
            <FormSection>
              {!disableRename && (
                <ValidatedInput
                  type="text"
                  name="name"
                  label="Name"
                  formGroupClassName="mr-3"
                />
              )}
              {showPublishButton && (
                <FormGroup>
                  <Label check className="d-flex align-items-center">
                    <Input
                      className="ml-0 mr-1 my-0"
                      tag={Field}
                      name="isPublished"
                      type="checkbox"
                    />
                    Published
                  </Label>
                </FormGroup>
              )}
              {showGrowingZones && (
                <WideFormGroup $maxWidth="720px">
                  <Label>Growing Zones</Label>
                  <FormTypeahead
                    id="growing-zones"
                    options={GROWING_ZONES}
                    name="growingZones"
                    size="sm"
                    label="Growing Zones"
                    className="mr-3"
                    multiple
                    closeMenuOnBlur={false}
                  />
                </WideFormGroup>
              )}
              <div className="d-flex">
                <ValidatedInput
                  type="number"
                  name="betweenRowSpacing"
                  label="Between Row Spacing"
                  min={0}
                  step="any"
                  unitSpec={DISTANCE_UNITS}
                  maxWidth="130px"
                />
                <ValidatedInput
                  type="number"
                  name="inRowSpacing"
                  label="In Row Spacing"
                  min={0}
                  step="any"
                  unitSpec={DISTANCE_UNITS}
                  maxWidth="130px"
                />
              </div>
              <div className="d-flex flex-wrap">
                {/* Put unit cost on its own row for spacing purposes if name field not shown */}
                <div className={disableRename ? 'w-100' : undefined}>
                  <ValidatedInput
                    type="number"
                    name="unitCost"
                    label="Unit cost"
                    min={0}
                    step={0.01}
                    unit="/ea"
                    currency
                    maxWidth="110px"
                  />
                </div>
                <ValidatedInput
                  type="number"
                  name="mortality"
                  label="Mortality"
                  step="any"
                  unit="%"
                  maxWidth="110px"
                />
                <ValidatedInput
                  type="number"
                  name="mortalityEscalator"
                  label="Mortality Escalator"
                  min={0}
                  step="any"
                  unit="%"
                  maxWidth="110px"
                />
              </div>
            </FormSection>
            <FormSection>
              <WideFormGroup $maxWidth="720px">
                <ValidatedInput
                  type="textarea"
                  name="description"
                  label="Description"
                  rows="3"
                  formGroupClassName="mr-3"
                />
              </WideFormGroup>
              <WideFormGroup $maxWidth="720px">
                <Label for="varieties-typeahead">Varieties</Label>
                <CropVarietiesTypeahead
                  id="varieties-typeahead"
                  className="mr-3"
                />
              </WideFormGroup>
            </FormSection>
          </div>
          <div className="d-flex flex-wrap">
            <div className="w-100">
              <h3>Yield</h3>
              <ValidatedInput
                type="number"
                name="yield.targetYieldPerTree"
                label="Target yield per tree"
                step="any"
                min={0}
                unitSpec={YIELD_UNITS_BY_DEFAULT[values?.yield?.unit]}
                unit="" // Hide units because they are displayed in the dropdown
              >
                {({ renderInput, renderError, renderLabel }) => (
                  <FormGroup>
                    {renderLabel()}
                    <div className="d-flex">
                      {renderInput()}
                      <DropdownButtonField
                        outline
                        size="sm"
                        className="ml-2"
                        options={yieldUnitOptions}
                        name="yield.unit"
                      />
                    </div>
                    {renderError()}
                  </FormGroup>
                )}
              </ValidatedInput>
            </div>
            <YieldSection />
          </div>
          {renderButtons && renderButtons()}
          {id && (
            <TableCsvButton
              className="mr-2"
              cols={exportCols}
              data={exportData(resource, yieldUnits, distanceUnits)}
              title={title}
            />
          )}
          {id && onDelete && (
            <DeleteButton
              onDelete={onDelete}
              disabled={isSubmitting || loading || !canBeDeleted}
              tooltipContent={deletionTooltipContent}
              className="mr-2"
            />
          )}
          <SubmitButton loading={loading} />
        </TemplateForm>
      )}
    </Formik>
  );
};

export const EditTemplateCropSettings = () => {
  const { id, teamId } = useParams();

  const props = useRecord({
    submitMutation: UpsertCropTemplateMutation,
    deleteMutation: DeleteCropTemplateMutation,
    fetchRecordQuery: CropTemplateQuery,
    onDeleteRefetchQuery: CropTemplatesQuery,
    creationRedirectUrl: '../:id/edit',
    deletionRedirectUrl: '../..',
    transformValues,
  });

  const {
    status: { loading },
    mutateAndSetState,
  } = props;

  const renderDuplicateButton = () => (
    <DuplicateTemplateButton
      id={id}
      mutateAndSetState={mutateAndSetState}
      loading={loading}
      refetchQuery={CropTemplatesQuery}
    />
  );

  return (
    <EditCropSettings
      renderButtons={renderDuplicateButton}
      {...props}
      showGrowingZones
      showPublishButton={id && !teamId}
    />
  );
};

export const EditFarmCropSettings = () => {
  const { id, farmId } = useParams();
  const basePath = getFarmPath(farmId, 'data/crops');

  const props = useRecord({
    deleteMutation: DeleteFarmCropMutation,
    submitMutation: UpsertFarmCropMutation,
    fetchRecordQuery: RawCropQuery,
    onDeleteRefetchQuery: RawCropsQuery,
    creationRedirectUrl: `${basePath}/:id/edit`,
    deletionRedirectUrl: '../..',
    transformValues,
    includeFarmId: true,
  });

  const { data: cropData } = useQueryWithFarmId(CropsForFarmQuery);

  const {
    status: { loading },
    resource,
    mutateAndSetState,
  } = props;

  const isActiveCrop = useMemo(
    () =>
      cropData &&
      cropData?.crops.some(
        (crop) => crop.id === id && crop.name === resource?.name,
      ),
    [cropData, id, resource],
  );

  const renderButtons = () =>
    id && (
      <CropActionsDropdown
        resource={resource}
        refetchQuery={RawCropQuery}
        mutateAndSetState={mutateAndSetState}
        loading={loading}
      />
    );
  return (
    <EditCropSettings
      renderButtons={renderButtons}
      disableRename={isActiveCrop}
      {...props}
    />
  );
};
